// src/pages/Home.jsx
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import hero from "../assets/hero-growth.jpg";
import founder from "../assets/founder3.jpg";
import compassImg from "../assets/compass.png";
import { FaWhatsapp, FaLinkedin } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import supabase from "../supabaseClient";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

/* TypingText */
function TypingText({ text, speed = 40, className = "" }) {
  const [display, setDisplay] = useState("");
  useEffect(() => {
    let i = 0;
    let mounted = true;
    function step() {
      if (!mounted) return;
      if (i <= text.length) {
        setDisplay(text.slice(0, i));
        i += 1;
        setTimeout(step, speed);
      }
    }
    step();
    return () => {
      mounted = false;
    };
  }, [text, speed]);
  return <div className={className}>{display}</div>;
}

export default function Home() {
  const location = useLocation();
  const [testimonials, setTestimonials] = useState([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    async function fetchTestimonials() {
      const { data, error } = await supabase.from("testimonials").select("*");
      if (!error) setTestimonials(data);
    }
    fetchTestimonials();
  }, []);

  // existing scroll-to-on-nav-state behavior
  useEffect(() => {
    const scrollTo = location.state?.scrollTo;
    if (scrollTo) {
      requestAnimationFrame(() => {
        const el = document.getElementById(scrollTo);
        if (el) {
          const y = el.getBoundingClientRect().top + window.pageYOffset - 88;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
        window.history.replaceState({}, document.title);
      });
    }
  }, [location]);

  // IntersectionObserver for fade-in on scroll
  useEffect(() => {
    // Respect reduced motion
    const reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      // If user prefers reduced motion, reveal all immediately
      document
        .querySelectorAll(".fade-on-scroll")
        .forEach((el) => el.classList.add("in-view"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            // Add class to trigger CSS transitions
            el.classList.add("in-view");

            // If this element has children with .fade-stagger, they will animate using CSS var --i
            // If you want JS-based stagger you could iterate children and set timeouts here.

            obs.unobserve(el); // one-time reveal
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -8% 0px",
      }
    );

    const targets = document.querySelectorAll(".fade-on-scroll");
    targets.forEach((t) => observer.observe(t));

    return () => observer.disconnect();
  }, []);

  return (
    <div >
      {/* HERO */}
      <section
        id="home"
        className="max-w-7xl mx-auto rounded-2xl overflow-hidden card-glow fade-on-scroll "
      >
        <div className="relative grid md:grid-cols-2 items-center">
          {/* left content */}
          <div className="px-6 py-8 md:py-14 md:px-12 z-10">
            <h1
              className="text-3xl md:text-4xl h-font font-bold leading-tight fade-stagger"
              style={{ color: "var(--brand-deep)", "--i": 0 }}
            >
              Focus on what you do the best,
            </h1>
            <div className=" typing-text a11y-readable">
            <TypingText 
              text={" and let us focus on growing your wealth."}
              speed={50}
              className="mt-4 block text-2xl md:text-3xl text-green-600 font-semibold b-font fade-stagger"
              style={{ "--i": 1 }}
            />
            </div>
            <p
              className="mt-5 max-w-xl text-slate-600 b-font fade-stagger"
              style={{ "--i": 2 }}
            >
              <strong>Apar Growth Partners </strong>is an investment management
              firm based in Mumbai, India. It has been registered with the SEBI
              as an Investment Adviser, under individual license category in the
              name of Aman Shah.
            </p>

            <div
              className="mt-6 text-sm text-slate-600 b-font flex gap-6 fade-stagger"
              style={{ "--i": 3 }}
            >
              <div>
                <strong>SEBI Reg :</strong> INA000020730
              </div>
            </div>
          </div>

          {/* right hero image */}
          <div className="relative fade-stagger" style={{ "--i": 4 }}>
            <div className="absolute inset-0 hero-grad pointer-events-none"></div>
            <img
              src={hero}
              alt=""
              className="w-full h-full min-h-[300px] object-cover rounded-r-2xl"
            />
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="max-w-7xl mx-auto mt-8 section-tight grid gap-6 fade-on-scroll px-6">
        <div className="text-center">
          <h2
            className="text-4xl font-semibold h-font fade-stagger"
            style={{ color: "var(--brand-deep)", "--i": 0 }}
          >
            What we do
          </h2>
          <p
            className="mt-2 text-slate-600 b-font fade-stagger"
            style={{ "--i": 1 }}
          >
            We specialize in direct equity investment advisory services, helping
            clients grow their wealth through in-depth fundamental research and
            a disciplined approach. Our investment decisions are never guided by
            stock tips or rumours, but by our own research. When you invest with
            us, you gain a trusted partner committed to growing your wealth with
            clarity, transparency, and conviction.
          </p>
        </div>
      </section>

      {/* OUR APPROACH */}
      <section className="section-medium px-6 max-w-7xl mx-auto">
        <h2
          className="text-4xl font-semibold h-font fade-stagger text-center mb-8"
          style={{ color: "var(--brand-deep)", "--i": 0 }}
        >
          Our Approach
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-5 rounded-xl glass card-glow hover-lift">
            <h3 className="font-semibold text-lg mb-2">
              Strong roots in value-style investing
            </h3>
            <p className="text-slate-700">
              We remain firmly grounded in the discipline of value investing. No
              matter how appealing an idea may seem, we let it pass if it
              doesn’t meet our valuation standards.
            </p>
          </div>

          <div className="p-5 rounded-xl glass card-glow hover-lift">
            <h3 className="font-semibold text-lg mb-2">Skin in the game</h3>
            <p className="text-slate-700">
              We firmly believe in the principle of ‘skin in the game.’ In most
              cases, we hold the same positions that we recommend to our
              clients. This creates a strong alignment of interest between us
              and our clients.
            </p>
          </div>

          <div className="p-5 rounded-xl glass card-glow hover-lift">
            <h3 className="font-semibold text-lg mb-2">
              Customised portfolio construction
            </h3>
            <p className="text-slate-700">
              Each client’s situation is unique, so we design customised
              portfolios tailored to their goals, risk profile, and liquidity
              needs—never a one-size-fits-all model portfolio.
            </p>
          </div>
        </div>
      </section>

      {/* COMPASS */}
      <section id="compass" className="w-full flex justify-center">
        <div
          className="bg-box relative rounded-2xl overflow-hidden w-full max-w-6xl mx-4 md:mx-8 my-8"
          style={{
            backgroundImage: `linear-gradient(rgba(9,20,28,0.68), rgba(9,20,28,0.68)), url(${compassImg})`,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            minHeight: "380px",
          }}
        >
          <div className="py-10 md:py-16 px-4 sm:px-6 lg:px-10 relative z-10">
            <header className="mb-6 text-center">
              <h2 className="text-3xl md:text-4xl font-semibold h-font text-white">
                Our Compass
              </h2>
              <p className="mt-1 text-slate-200 max-w-2xl mx-auto">
                The essence of our wealth management philosophy is best
                reflected in the quotes below:
              </p>
            </header>

            <div className="mx-auto max-w-5xl grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 items-stretch">
              <div className="p-6 rounded-xl glass-dark card-glow hover-lift">
                <h3 className="font-semibold text-white mb-2">1. Be Patient</h3>
                <p className="font-semibold text-slate-200 italic">
                  “The stock market is a device to transfer money from the
                  impatient to the patient.” — Warren Buffett
                </p>
                <p className="mt-3 text-slate-100">
                  It is rare to find anyone who hasn’t made money in the stock
                  market by being patient. Patience, therefore, is the first
                  step toward building lasting wealth.
                </p>
              </div>

              <div className="p-6 rounded-xl glass-dark card-glow hover-lift">
                <h3 className="font-semibold text-white mb-2">
                  2. Focus on Long term compounding
                </h3>
                <p className="font-semibold text-slate-200 italic">
                  “In the short run, the market is a voting machine but in the
                  long run, it is a weighing machine.” — Benjamin Graham
                </p>
                <p className="mt-3 text-slate-100">
                  In the short run, market values are driven by sentiments,
                  while in the long run, they reflect intrinsic value.
                  Therefore, a fundamental understanding of a company’s true
                  value yields greater rewards over time.
                </p>
              </div>

              <div className="p-6 rounded-xl glass-dark card-glow hover-lift">
                <h3 className="font-semibold text-white mb-2">
                  3. Have Margin of Safety
                </h3>
                <p className="font-semibold text-slate-200 italic">
                  “You don’t drive a truck that weighs 9000 pounds across a
                  bridge that says ‘limit 10,000 pounds’…” — Warren Buffett
                </p>
                <p className="mt-3 text-slate-100">
                  The quote holds true not only for investing but also for life.
                  In investing, it is essential to maintain adequate buffers —
                  what we call a margin of safety to account for situations that
                  don't unfold as expected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT AMAN SHAH */}
      <section
        id="about"
        className="max-w-7xl mx-auto mt-8 section-medium fade-on-scroll"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {/* Founder card (mobile shows full image) */}
          <div
            className="order-first md:order-last fade-stagger"
            style={{ "--i": 0 }}
          >
            <div className="glass card-glow hover-lift p-4 h-full flex items-start md:items-center">
              <div className="w-full">
                {/* Image wrapper ensures full image visible on small screens */}
                <div
                  className="w-full overflow-hidden rounded-md bg-white flex items-center justify-center"
                  style={{ minHeight: 0 }}
                >
                  {/* object-contain -> full photo visible on mobile; md+: object-cover for balanced card */}
                  <img
                    src={founder}
                    alt="Aman Shah"
                    loading="lazy"
                    className="w-full max-w-full h-auto md:h-[320px] lg:h-[360px] object-contain md:object-cover rounded-md"
                    style={{ objectPosition: "center top" }}
                  />
                </div>

                <div className="mt-3 px-1">
                  <h4 className="font-semibold">Aman Shah</h4>
                  <div className="text-sm text-slate-600">
                    Founder & Lead Adviser
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* About text content */}
          <div className="md:col-span-2 fade-stagger" style={{ "--i": 1 }}>
            <div className="glass card-glow hover-lift p-6 h-full flex flex-col">
              <h3
                className="text-2xl font-semibold h-font"
                style={{ color: "var(--brand-deep)" }}
              >
                About Aman Shah
              </h3>
              <div className="mt-4 flex-1">
                <p className="text-slate-700 b-font">
                  Qualifications: CA, CFA (USA). Experience: 8+ years in
                  fundamental equity research and investment advisory.
                </p>

                <p className="mt-4 text-slate-700 b-font">
                  Aman blends academic rigor with practical market experience.
                  His interest in analysing businesses and stock markets began
                  during his college years at R. A. Podar College, Mumbai, and
                  has since grown into a passion for identifying long-term
                  investment opportunities.
                </p>

                <p className="mt-4 text-slate-700 b-font">
                  He has been moulded in his initial years, at Jeetay
                  Investments Private Ltd., a SEBI-registered Portfolio
                  Management Services (PMS). At Jeetay, he researched on a wide
                  range of industries like BFSI, Capital goods, Hospitality, EPC
                  companies, etc. and also assisted the fund manager in
                  portfolio management. This dual exposure gave him both a
                  stock-level perspective and a broader understanding of
                  portfolio construction.
                </p>

                <p className="mt-4 text-slate-700 b-font">
                  At Apar, Aman attempts to guide his clients on their journey
                  toward sustainable wealth creation, with patience and
                  discipline at the core of his approach.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ⭐ TESTIMONIALS SECTION ⭐ */}
      <section id="testimonials"
      className="max-w-7xl mx-auto py-20 px-6 fade-on-scroll">
        <h2
          className="text-3xl md:text-4xl font-semibold text-center h-font"
          style={{ color: "var(--brand-deep)" }}
        >
          What Our Clients Say
        </h2>

        <p className="text-center text-slate-600 mt-2">
          We take pride in doing the right thing for our clients — Hear from
          them:
        </p>

        {/* Slider */}
        {testimonials.length > 0 && (
          <div className="relative mt-12 flex justify-center items-center">
            {/* Left Button */}
            <button
              onClick={() =>
                setIndex(
                  (index - 1 + testimonials.length) % testimonials.length
                )
              }
              className="absolute left-2 md:left-12 w-10 h-10 flex items-center justify-center
                   bg-white shadow rounded-full hover:shadow-lg transition"
              aria-label="Previous testimonial"
            >
              <FaArrowLeft className="text-slate-600" aria-hidden="true" />
            </button>

            {/* Card */}
            <div className="w-[90%] max-w-3xl p-8 glass rounded-2xl shadow-md min-h-[260px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* User Info */}
                  <div className="flex flex-col items-center text-center">
                    <img
                      src={testimonials[index].avatar_url}
                      alt={testimonials[index].name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-green-600 shadow-sm"
                    />

                    <h3 className="font-semibold text-lg text-slate-800 mt-3">
                      {testimonials[index].name}
                    </h3>

                    <p className="text-sm text-slate-500 -mt-1">
                      {testimonials[index].designation}
                    </p>

                    <p className="text-slate-700 mt-4 italic text-[1.05rem] leading-relaxed max-w-2xl">
                      “{testimonials[index].feedback}”
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Right Button */}
            <button
              onClick={() => setIndex((index + 1) % testimonials.length)}
              className="absolute right-2 md:right-12 w-10 h-10 flex items-center justify-center
                   bg-white shadow rounded-full hover:shadow-lg transition"
              aria-label="Next testimonial"
            >
              <FaArrowRight className="text-slate-600" aria-hidden="true" />
            </button>
          </div>
        )}

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {testimonials.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition ${
                i === index ? "bg-green-600" : "bg-slate-300"
              }`}
            />
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contact"
        className="max-w-7xl mx-auto mt-8 section-tight text-center px-6 fade-on-scroll"
      >
        <header className="text-center mb-6">
          <h2
            className="text-3xl md:text-4xl font-semibold h-font text-green-700 fade-stagger"
            style={{ "--i": 0 }}
          >
            Contact Us
          </h2>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Left Card - SEBI Info */}
          <div
            className="glass card-glow p-6 rounded-xl fade-stagger text-left overflow-x-auto"
            style={{ "--i": 0 }}
          >
            <h3 className="font-semibold mb-4 text-lg text-green-700">
              SEBI Registered Investment Advisor Details
            </h3>

            <table className=" tables a11y-readable w-full text-sm text-slate-700 border border-slate-200 rounded-lg">
              <tbody>
                <tr className="border-b border-slate-200">
                  <td className="font-medium p-2">Name</td>
                  <td className="p-2">Aman Rakesh Shah</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="font-medium p-2">Type of Registration</td>
                  <td className="p-2">Individual</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="font-medium p-2">SEBI Registration No</td>
                  <td className="p-2 font-semibold text-green-700">
                    INA000020730
                  </td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="font-medium p-2">Validity of Registration</td>
                  <td className="p-2">Jul 31, 2025 - Perpetual</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="font-medium p-2">Office Address</td>
                  <td className="p-2">
                    Unit No. 129, Gala Industrial Estate, 2nd floor,
                    <br /> Dindayal Upadhyay Marg, Mulund (West), Mumbai – 400080
                  </td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="font-medium p-2">Email</td>
                  <td className="p-2">ca.aman.shah@gmail.com</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="font-medium p-2">Trade Name</td>
                  <td className="p-2">Apar Growth Partners</td>
                </tr>
                <tr>
                  <td className="font-medium p-2">Compliance Officer</td>
                  <td className="p-2">Aman Rakesh Shah</td>
                </tr>
              </tbody>
            </table>

            {/* Contact Links */}
            <div className="mt-5 flex flex-col gap-3 items-start">
              <a
                href="mailto:ca.aman.shah@gmail.com"
                className="flex items-center gap-2 text-slate-700 hover:text-green-700 transition"
              >
                <MdEmail size={20} className="text-green-600" aria-hidden="true" />
                <span className="underline underline-offset-2">ca.aman.shah@gmail.com</span>
              </a>

              <a
                href="https://wa.me/9867066755"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-slate-700 hover:text-green-700 transition"
              >
                <FaWhatsapp size={20} className="text-green-600" title="WhatsApp" />
                <span className="underline underline-offset-2">+91 98670 66755</span>
              </a>

              <a
                href="https://www.linkedin.com/in/aman-shah-078501a9"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-slate-700 hover:text-green-700 transition"
              >
                <FaLinkedin size={20} className="text-green-600" aria-hidden="true" />
                <span className="underline underline-offset-2">LinkedIn Profile</span>
              </a>
            </div>
          </div>

          {/* Right Card - Contact Form */}
          <div
            className="glass card-glow p-6 rounded-xl fade-stagger"
            style={{ "--i": 1 }}
          >
            <h3 className="font-semibold mb-2">Get in Touch</h3>
            <form
              action="https://formspree.io/f/xpwydkpa"
              method="POST"
              className="space-y-4"
            >
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-slate-600 mb-1">
                  Name
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  autoComplete="name"
                  required
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-green-600 outline-none"
                  placeholder="Your Name"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-slate-600 mb-1">
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-green-600 outline-none"
                  placeholder="Your Email"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-slate-600 mb-1">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  rows="4"
                  name="message"
                  required
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-green-600 outline-none"
                  placeholder="Your Message"
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-green-700 hover:bg-green-800 text-white font-semibold py-3 rounded-lg transition"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
